import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useHistory, useParams, useLocation } from "react-router-dom";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import OfferOverview from "./OfferOverview";
import ContentTargeting from "./ContentTargeting";
import ContentGuidelines from "./ContentGuidelines";
import { useSelector } from "react-redux";
import ReusableMuiIcons from "../../_helpers/ReusableMuiIcons";
import FirebaseImageStore from "../../_helpers/reusablefunctions/firebaseImageStore";
import useSnackDispatcher from "../../_helpers/reusablefunctions/snackBarComponent";
import { formSnackbar } from "../../_services/_constants/snackbar.constant";
import { useMultiStepFormHandler } from "../../hooks/useMultistepFormHandler";
import {
  validateOverviewStep,
  validateTargetingStep,
  validateGuidelinesStep,
} from "../../_helpers/validationPage";
import { updateProductImages as updateImageApiCall } from "../../_helpers/reusablefunctions/updateImageApiCall";
import ApiService from "../../_services/ApiService";
import ConfirmationDialog from "../../components/reusableComponents/conformationDialogBox";
import ConfirmMessageBox from '../../components/reusableComponents/confirmMessageBox';
import OfferSuccessModal from "../../components/reusableComponents/offerSuccessModal";
const OfferComponent = () => {
  const { stepName, campaignId, offerId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { successSnack, errorSnack } = useSnackDispatcher();
  const userData = useSelector(state => state.userData.userData);

  const isEditMode = location.pathname.startsWith('/edit-offer');

  const [activeStep, setActiveStep] = useState(0);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [selectedProductFiles, setSelectedProductFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [sections, setSections] = useState([{ format: "", quantities: "" }]);
  const [referenceLinks, setReferenceLinks] = useState([""]);
  const [initialState, setInitialState] = useState({});
  const [existingProductImageIds, setExistingProductImageIds] = useState([]);
  const [showCustomConfirm, setShowCustomConfirm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [finalSubmitEvent, setFinalSubmitEvent] = useState(null);

  const steps = ["overview", "targeting", "guidelines"];
  const stepLabels = ["Offer Overview", "Deliverables & Targeting", "Guidelines & FAQ"];
  const stepMap = { overview: 0, targeting: 1, guidelines: 2 };

  const {
    formData,
    setFormData,
    submitOfferDetails,
    loader,
    isDraft,
    setIsDraft,
    fetchOfferDetails,
    barterId,
    isDataFetched,
    uploadedProductImageUrls,
    setUploadedProductImageUrls
  } = useMultiStepFormHandler({
    userData,
    campaignTypeFromUrl: campaignId,
    activeStep,
    steps,
    history,
    successSnack,
    errorSnack,
    formSnackbar,
    isEditMode,
  });

  const getExistingImages = () => selectedProductFiles.filter(file => typeof file === 'string');
  const getNewImages = () => selectedProductFiles.filter(file => typeof file !== 'string');

  const { uploadImages, uploadSingleImage } = FirebaseImageStore({
    selectedFile: selectedBannerFile,
    selectedFiles: getNewImages(),
    functionCall: async ({ bannerURL, productImages }, draft) => {
      const existingImageUrls = getExistingImages();
      const allProductImages = [...existingImageUrls, ...productImages];

      const success = await submitOfferDetails({
        bannerURL,
        draft,
        productImages: allProductImages,
      });

      if (success) {
        if (draft) {
          successSnack("Offer saved as draft successfully.");
          history.push("/new-offer");
        } else if (!draft && activeStep < steps.length - 1) {
          const nextStep = activeStep + 1;
          const path = isEditMode
            ? `/edit-offer/${steps[nextStep]}/${offerId}`
            : `/create-offer/${steps[nextStep]}/${campaignId}`;
          history.push(path);
        }
      }
    },
  });


  useEffect(() => {
    if (isEditMode && offerId) {
      fetchOfferDetails(offerId).then((res) => {
        if (res) {
          setSelectedProductFiles(res.productImageUrls);
          setExistingProductImageIds(res.productImageIds);
          setUploadedProductImageUrls(res.productImageUrls);
        }
      });
    }
  }, [isEditMode, offerId]);

  useEffect(() => {
    if (isDataFetched) {
      setInitialState({
        formData,
        selectedBannerFile,
        selectedProductFiles,
        sections,
        referenceLinks,
      });
    }
  }, [isDataFetched, formData, selectedBannerFile, selectedProductFiles, sections, referenceLinks]);

  const hasUnsavedChanges = () =>
    JSON.stringify({
      formData,
      selectedBannerFile,
      selectedProductFiles,
      sections,
      referenceLinks,
    }) !== JSON.stringify(initialState);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        const message = "You have unsaved changes. Are you sure you want to leave?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formData, selectedBannerFile, selectedProductFiles, sections, referenceLinks, initialState]);

  useEffect(() => {
    if (stepName && stepMap[stepName] !== undefined) {
      setActiveStep(stepMap[stepName]);
    }
  }, [stepName]);

  const handleDeleteImage = async (index) => {
    const imageToDelete = selectedProductFiles[index];

    if (typeof imageToDelete === "string") {
      const imageId = existingProductImageIds[index];

      try {
        await ApiService.delete("product_images", {
          ids: imageId,
          barter: offerId
        });
        successSnack("Image deleted successfully");

        setSelectedProductFiles(prev => prev.filter((_, i) => i !== index));
        setExistingProductImageIds(prev => prev.filter((_, i) => i !== index));
      } catch (err) {
        errorSnack("Failed to delete image.");
      }
    } else {
      setSelectedProductFiles(prev => prev.filter((_, i) => i !== index));
      setFormData(prev => ({
        ...prev,
        product_images: prev.product_images?.filter((_, i) => i !== index) || []
      }));
    }
  };

  const handleUpdateImage = async (index, newFile) => {
    const imageId = existingProductImageIds[index];

    if (!imageId) {
      return;
    }

    try {
      const downloadURL = await uploadSingleImage(newFile);

      if (!downloadURL) {
        errorSnack("Image upload failed.");
        return;
      }

      const res = await updateImageApiCall({
        barterId: offerId,
        productImageUrls: [downloadURL],
        imageIds: [imageId],
      });

      if (res?.status === 200 || res?.status === 204) {
        successSnack("Image updated successfully.");
        setSelectedProductFiles((prev) =>
          prev.map((item, i) => (i === index ? downloadURL : item))
        );

        setUploadedProductImageUrls(prev => {
          const newUrls = [...prev];
          newUrls[index] = downloadURL;
          return newUrls;
        });

        setInitialState(prevState => ({
          ...prevState,
          selectedProductFiles: selectedProductFiles.map((item, i) =>
            i === index ? downloadURL : item
          )
        }));
      } else {
        errorSnack("Failed to update image.");
      }
    } catch (error) {
      errorSnack("Something went wrong during image update.");
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges()) {
      const confirmLeave = window.confirm("You have unsaved changes. Do you really want to go back?");
      if (!confirmLeave) return;
    }
    history.push("/new-offer/campaign-type");
  };

  const handleImageUploadLogic = async (draft = false) => {
    const existingImages = getExistingImages();
    const newImages = getNewImages();
    const hasBannerFile = selectedBannerFile !== null;
    const hasNewImages = newImages.length > 0;

    const unUploadedImages = existingImages.filter(
      url => !uploadedProductImageUrls.includes(url)
    );

    if (!hasNewImages && !hasBannerFile && unUploadedImages.length === 0) {
      return await submitOfferDetails({
        draft,
        productImages: existingImages
      });
    }

    return await uploadImages(draft);
  };


  const handleSaveDraft = async () => {
    setIsDraft(true);
    let errors = {};

    if (activeStep === 0) {
      errors = validateOverviewStep(formData, selectedBannerFile, selectedProductFiles, true);
    } else if (activeStep === 1) {
      errors = validateTargetingStep(formData, sections, true);
    } else if (activeStep === 2) {
      errors = validateGuidelinesStep(formData, referenceLinks, true);
    }

    // setFormErrors(errors);
    // if (Object.keys(errors).length > 0) return;
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).flat(); // handles arrays like reference_links
      const hasRequiredError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("required") || msg.toLowerCase().includes("must be"))
      );
      const hasInvalidError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("not valid"))
      );

      if (hasRequiredError) {
        errorSnack("Please fill all mandatory fields.");
      } else if (hasInvalidError) {
        errorSnack("Some fields contain invalid values. Please correct them.");
      } else {
        errorSnack("Some fields contain invalid values. Please correct them.");
      }

      return;
    }

    let success = false;

    if (activeStep === 0) {
      success = await handleImageUploadLogic(true);
    } else {
      success = await submitOfferDetails({ draft: true });
    }

    if (success) {
      if (activeStep === 0 || activeStep === 1 || activeStep === 2) {
        localStorage.removeItem("barterId");
      }
      successSnack("Offer saved as draft successfully.");
      history.push("/view-offer-drafts");
    }
  };

  const proceedFinalSubmission = async ({ e, isFinalSubmit }) => {
    if (!e.defaultPrevented) e.preventDefault();
    setIsDraft(false);

    let errors = {};

    if (activeStep === 0) {
      errors = validateOverviewStep(formData, selectedBannerFile, selectedProductFiles, false);
    } else if (activeStep === 1) {
      errors = validateTargetingStep(formData, sections, false);
    } else if (activeStep === 2) {
      errors = validateGuidelinesStep(formData, referenceLinks, false);
    }

    // setFormErrors(errors);
    // if (Object.keys(errors).length > 0) return;
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).flat(); // handles arrays like reference_links
      const hasRequiredError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("required") || msg.toLowerCase().includes("must be"))
      );
      const hasInvalidError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("not valid"))
      );

      if (hasRequiredError) {
        errorSnack("Please fill all mandatory fields.");
      } else if (hasInvalidError) {
        errorSnack("Some fields contain invalid values. Please correct them.");
      } else {
        errorSnack("Please fill all mandatory fields.");
      }

      return;
    }

    let success = false;

    if (activeStep === 0) {
      success = await handleImageUploadLogic(false);
    } else {
      success = await submitOfferDetails({ draft: false });
    }

    if (success) {
      if (!isFinalSubmit && activeStep < steps.length - 1) {
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);
        const path = isEditMode
          ? `/edit-offer/${steps[nextStep]}/${offerId}`
          : `/create-offer/${steps[nextStep]}/${campaignId}`;
        history.push(path);
      }

      if (isFinalSubmit) {
        setShowSuccessModal(true);
      }
    }
  };

  // const handleSubmitOffer = (e, isFinalSubmit) => {
  //   if (isFinalSubmit) {
  //     setFinalSubmitEvent({ e, isFinalSubmit });
  //     setShowCustomConfirm(true);
  //     return;
  //   }
  //   proceedFinalSubmission({ e, isFinalSubmit });
  // };

  const handleSubmitOffer = async (e, isFinalSubmit) => {
    if (!e.defaultPrevented) e.preventDefault();

    setIsDraft(false);
    let errors = {};

    if (activeStep === 0) {
      errors = validateOverviewStep(formData, selectedBannerFile, selectedProductFiles, false);
    } else if (activeStep === 1) {
      errors = validateTargetingStep(formData, sections, false);
    } else if (activeStep === 2) {
      errors = validateGuidelinesStep(formData, referenceLinks, false);
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).flat();
      const hasRequiredError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("required") || msg.toLowerCase().includes("must be"))
      );
      const hasInvalidError = errorMessages.some(msg =>
        typeof msg === "string" &&
        (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("not valid"))
      );

      if (hasRequiredError) {
        errorSnack("Please fill all mandatory fields.");
      } else if (hasInvalidError) {
        errorSnack("Some fields contain invalid values. Please correct them.");
      } else {
        errorSnack("Please fill all mandatory fields.");
      }

      return;
    }

    if (isFinalSubmit) {
      setFinalSubmitEvent({ e, isFinalSubmit });
      setShowCustomConfirm(true);
    } else {
      proceedFinalSubmission({ e, isFinalSubmit });
    }
  };


  const handleTabChange = async (e, index) => {
    if (index === activeStep) return;

    if (index < activeStep) {
      setActiveStep(index);
      const path = isEditMode
        ? `/edit-offer/${steps[index]}/${offerId}`
        : `/create-offer/${steps[index]}/${campaignId}`;
      history.push(path);
      return;
    }

    let errors = {};

    if (activeStep === 0) {
      errors = validateOverviewStep(formData, selectedBannerFile, selectedProductFiles, false);
    } else if (activeStep === 1) {
      errors = validateTargetingStep(formData, sections, false);
    } else if (activeStep === 2) {
      errors = validateGuidelinesStep(formData, referenceLinks, false);
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    let success = false;

    if (activeStep === 0) {
      success = await handleImageUploadLogic(true);
    } else {
      success = await submitOfferDetails({ draft: true });
    }

    if (success) {
      setActiveStep(index);
      const path = isEditMode
        ? `/edit-offer/${steps[index]}/${offerId}`
        : `/create-offer/${steps[index]}/${campaignId}`;
      history.push(path);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <OfferOverview
            formData={formData}
            setFormData={setFormData}
            selectedBannerFile={selectedBannerFile}
            setSelectedBannerFile={setSelectedBannerFile}
            selectedProductFiles={selectedProductFiles}
            setSelectedProductFiles={setSelectedProductFiles}
            isDataFetched={isDataFetched}
            isEditMode={isEditMode}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            onDelete={handleDeleteImage}
            onEdit={isEditMode ? handleUpdateImage : undefined}
          />
        );
      case 1:
        return (
          <ContentTargeting
            formData={formData}
            setFormData={setFormData}
            barterId={barterId}
            isEditMode={isEditMode}
            sections={sections}
            setSections={setSections}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        );
      case 2:
        return (
          <ContentGuidelines
            formData={formData}
            setFormData={setFormData}
            barterId={barterId}
            isEditMode={isEditMode}
            referenceLinks={referenceLinks}
            setReferenceLinks={setReferenceLinks}
            offerId={offerId}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box p={4}>
      <Box sx={{ display: "flex" }}>
        <ReusableHeadingComponents
          bacHistory={true}
          clickHandlerBack={handleBack}
          heading={campaignId ? 'Create Barter Offer ' : 'Edit Barter Offer'}
          subHeading={' Follow the steps below to create your campaign'}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          mt: { xs: '20px', md: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
          {stepLabels.map((label, index) => (
            <React.Fragment key={index}>
              <Box
                onClick={() => handleTabChange(null, index)}
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: index === activeStep ? '3px solid #F14950' : '2px solid transparent',
                  color: index <= activeStep ? '#F14950' : '#8C8C8C',
                  fontWeight: 500,
                  fontSize: '14px',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': { color: '#f44336' },
                }}
              >
                {label}
              </Box>
              {index < stepLabels.length - 1 && (
                <Box sx={{ color: index < activeStep ? '#F14950' : '#1C1B1F', mx: '8px' }}>
                  <ReusableMuiIcons name="arrowright" />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <ReusableButton
            children={'Save Draft'}
            variant="outlined"
            classNames={'gn-actionbtn-outline gn-actionbtn-color me-3'}
            onClick={handleSaveDraft}
            loader={isDraft && loader}
            disabled={loader}
          />
          <ReusableButton
            children={activeStep === steps.length - 1 ? 'Submit Offer' : 'Save & Continue'}
            variant="contained"
            classNames={'gn-actionbtn'}
            onClick={(e) => handleSubmitOffer(e, activeStep === steps.length - 1)}
            loader={!isDraft && loader}
            disabled={loader}
          />
        </Box>
      </Box>

      <Box sx={{
        backgroundColor: "#E6ECFF",
        borderRadius: 1,
        p: { xs: 1, md: 1 },
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '3px', md: '10px' }
      }}>
        <Typography variant="h6" fontWeight="700" sx={{ color: "#626478", fontSize: { xs: '18px', md: '22px' } }}>
          Step {activeStep + 1} - {stepLabels[activeStep]}
          {activeStep === 0 && (
            <Typography
              component="span"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '13px', md: '15px' },
                ml: 1,
                color: "#626478",
              }}
            >
              (Assets & Key Details)
            </Typography>
          )}
        </Typography>
      </Box>
      <Box sx={{ mb: 4 }}>
        {renderStepContent()}
      </Box>

      <Box sx={{ display: "flex", justifyContent: 'flex-end', gap: 2, paddingBottom: ' 20px' }}>
        <ReusableButton
          children={'Save Draft'}
          variant="outlined"
          classNames={'gn-actionbtn-outline gn-actionbtn-color me-3'}
          onClick={handleSaveDraft}
          loader={isDraft && loader}
          disabled={loader}
        />
        <ReusableButton
          children={activeStep === steps.length - 1 ? 'Submit Offer' : 'Save & Continue'}
          variant="contained"
          classNames={'gn-actionbtn'}
          onClick={(e) => handleSubmitOffer(e, activeStep === steps.length - 1)}
          loader={!isDraft && loader}
          disabled={loader}
        />
      </Box>

      <ConfirmMessageBox
        open={showCustomConfirm}
        onClose={() => setShowCustomConfirm(false)}
        onSubmit={() => {
          setShowCustomConfirm(false);
          proceedFinalSubmission(finalSubmitEvent);
        }}
        title={"Once submitted, this offer cannot be edited."}
        message={"Make sure all details are accurate and complete."}
        children={"Publish Offer"}
        heading={"Confirm Offer Submission"}
        subHeading={"You are about to submit your offer for publishing. Please note:"}
      />

      <OfferSuccessModal
        open={showSuccessModal}
        onClose={() => history.push("/new-offer")}
      />
    </Box>
  );
};

export default OfferComponent;